/**
 * Framing landing page -> order page photo handoff (owner 2026-10-07).
 *
 * The framed-print product pages (theme template "framed-prints") let a
 * visitor try their own photo in every frame on the page. When they press an
 * order button, the page stores the File plus the frame they were looking at
 * in IndexedDB (same origin: both pages live on rewindphotolab.com.au) and
 * opens /pages/order-prints?...&from=framing&handoff=1.
 *
 * Here the order page takes that record ONCE (it is deleted on read), uploads
 * the photo through the normal addFiles path, waits for it to finish, then
 * opens Size & Paper with that photo selected and the frame already picked.
 * Anything missing or stale just leaves the customer on the normal flow.
 *
 * Writer: rewind-shopify-theme assets/frl-landing.js (keep the DB/store/key
 * names and the record shape in step). The photo is `file` (File/Blob), or
 * `bytes` (ArrayBuffer) when the browser cannot store a File (Safari Private
 * Browsing).
 */
import { framedProductList, type PaperId, type PricingData } from '@rpl/pricing';
import { useStore } from '../store/store';

export const HANDOFF_DB = 'rpl-handoff';
export const HANDOFF_STORE = 'items';
export const HANDOFF_KEY = 'framing';
/** A handoff older than this is ignored (a tab left open overnight). */
export const HANDOFF_MAX_AGE_MS = 2 * 60 * 60 * 1000;

export type HandoffTier = 'ctype' | 'standard' | 'premium';

export interface FramingHandoff {
  file: File;
  printSize: string;
  frameSize: string;
  border: string;
  tier: HandoffTier;
  colour: string;
}

/**
 * The paper each landing-page price tier opens on. Lustre for both behind-glass
 * tiers (glare), Cotton Rag Smooth (the Best Seller) for fine art. The customer
 * can still change paper in Size & Paper; the frame re-maps to it there.
 */
export const PAPER_FOR_TIER: Record<HandoffTier, PaperId> = {
  ctype: 'lustre',
  standard: 'bauhaus-lustre',
  premium: 'ilford-cottonrag-smooth',
};

const TIERS: readonly HandoffTier[] = ['ctype', 'standard', 'premium'];

/** True when the URL asks the order page to look for a framing handoff. */
export function wantsFramingHandoff(params: Pick<URLSearchParams, 'get'>): boolean {
  return params.get('handoff') === '1';
}

/** Raw photo bytes as stored by the landing page's fallback (ArrayBuffer or a typed array). */
function isBytes(v: unknown): v is ArrayBuffer | ArrayBufferView {
  return (v instanceof ArrayBuffer || ArrayBuffer.isView(v)) && v.byteLength > 0;
}

/** Validate a stored record. Pure (unit tested); null when unusable or stale. */
export function parseFramingHandoff(raw: unknown, now: number): FramingHandoff | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.ts !== 'number' || now - r.ts > HANDOFF_MAX_AGE_MS || r.ts - now > 60_000) return null;
  if (typeof Blob === 'undefined') return null;
  // The photo arrives as `file` (a File/Blob) or, from Safari Private Browsing,
  // as `bytes`: WebKit's private mode cannot store a File in IndexedDB
  // ("Error preparing Blob/File data to be stored in object store"), so the
  // landing page retries with the raw bytes (found 2026-10-30).
  const hasFile = r.file instanceof Blob && r.file.size > 0;
  if (!hasFile && !isBytes(r.bytes)) return null;
  const tier = r.tier as HandoffTier;
  if (!TIERS.includes(tier)) return null;
  for (const k of ['printSize', 'frameSize', 'border', 'colour'] as const) {
    if (typeof r[k] !== 'string' || !(r[k] as string)) return null;
  }
  const name = typeof r.name === 'string' && r.name ? r.name : 'photo.jpg';
  const stored = hasFile ? (r.file as Blob) : null;
  const type = typeof r.type === 'string' && r.type ? r.type : stored?.type || 'image/jpeg';
  const file =
    stored instanceof File
      ? stored
      : new File([(stored ?? r.bytes) as BlobPart], name, { type });
  return {
    file,
    printSize: r.printSize as string,
    frameSize: r.frameSize as string,
    border: r.border as string,
    tier,
    colour: r.colour as string,
  };
}

export interface HandoffPick {
  frameId: string;
  paper: PaperId;
  /** The Size & Paper apply-bar size for the PRINT inside the frame. */
  sizeId: string;
  colour: string;
}

/**
 * The framed product the landing page was showing, on the tier's default paper.
 * Pure (unit tested). Matches print size, frame size AND colour; null when the
 * catalogue no longer stocks that frame (the customer just picks again).
 */
export function resolveHandoffFrame(
  data: PricingData,
  h: Pick<FramingHandoff, 'printSize' | 'frameSize' | 'tier' | 'colour'>,
): HandoffPick | null {
  const paper = PAPER_FOR_TIER[h.tier];
  const fp = framedProductList(data).find(
    (p) => p.paper === paper && p.printSize === h.printSize && p.frameSize === h.frameSize && p.colour === h.colour,
  );
  if (!fp) return null;
  return { frameId: fp.frameId, paper, sizeId: `${h.tier === 'ctype' ? 'ct' : 'fa'}-${h.printSize}`, colour: fp.colour };
}

/** Read AND delete the stored handoff. Resolves null on any failure. */
export function takeFramingHandoff(now = Date.now()): Promise<FramingHandoff | null> {
  return new Promise((resolve) => {
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(HANDOFF_DB, 1);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(HANDOFF_STORE)) req.result.createObjectStore(HANDOFF_STORE);
    };
    req.onerror = () => resolve(null);
    req.onsuccess = () => {
      try {
        const store = req.result.transaction(HANDOFF_STORE, 'readwrite').objectStore(HANDOFF_STORE);
        const get = store.get(HANDOFF_KEY);
        get.onsuccess = () => {
          store.delete(HANDOFF_KEY); // one-shot: a refresh must not re-add the photo
          resolve(parseFramingHandoff(get.result, now));
        };
        get.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    };
  });
}

/** Wait for a photo to finish decode + hash + upload. */
function waitForPhoto(localId: string, maxMs = 180_000): Promise<'ready' | 'error' | 'gone' | 'timeout'> {
  return new Promise((resolve) => {
    const started = Date.now();
    const check = () => {
      const ph = useStore.getState().photos.find((p) => p.id === localId);
      if (!ph) return resolve('gone');
      if (ph.status === 'ready') return resolve('ready');
      if (ph.status === 'error') return resolve('error');
      if (Date.now() - started > maxMs) return resolve('timeout');
      setTimeout(check, 250);
    };
    check();
  });
}

/**
 * Wait until the order is loaded (phase 'ready' with a project). An expired
 * order shows the restart screen first; keep waiting while the customer starts
 * a fresh one. False on 'error' or after maxMs.
 */
function waitForProject(maxMs = 120_000): Promise<boolean> {
  return new Promise((resolve) => {
    const started = Date.now();
    const check = () => {
      const s = useStore.getState();
      if (s.phase === 'ready' && s.project) return resolve(true);
      if (s.phase === 'error' || Date.now() - started > maxMs) return resolve(false);
      setTimeout(check, 200);
    };
    check();
  });
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/apps/print/api';

/** True when the order page was opened from the framing landing page. */
export function cameFromFraming(params: Pick<URLSearchParams, 'get'>): boolean {
  return params.get('from') === 'framing';
}

let sourceTagged = false;

/**
 * Tag the order as started from the framing landing page (projects.source =
 * 'framing', 2026-10-21) so the weekly report can count framing-started
 * projects and orders. The Worker only fills an EMPTY source, so a gallery
 * seed or a share is never relabelled and a refresh is a no-op.
 * Fire-and-forget: every failure is swallowed, ordering must never break.
 */
export async function tagFramingSource(): Promise<boolean> {
  if (sourceTagged) return false;
  sourceTagged = true;
  try {
    if (!(await waitForProject())) return false;
    const handle = useStore.getState().project;
    if (!handle) return false;
    const res = await fetch(`${API_BASE}/projects/${handle.id}/source`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'X-RPL-Project-Token': handle.token },
      body: JSON.stringify({ source: 'framing' }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

let handoffStarted = false;

/**
 * Apply a framing-page handoff to the loaded project. Returns true when the
 * customer was taken to Size & Paper with the frame picked. On false they stay
 * on the upload screen, which already shows the photo's progress or error.
 * Runs once per page load (React StrictMode mounts effects twice in dev) and
 * only reads the stored photo once the order exists, so it cannot race boot.
 */
export async function applyFramingHandoff(): Promise<boolean> {
  if (handoffStarted) return false;
  handoffStarted = true;
  if (!(await waitForProject())) return false;
  const h = await takeFramingHandoff();
  if (!h) return false;
  await useStore.getState().addFiles([h.file]);
  // addFiles skips an identical re-upload (filename + bytes), so find the photo
  // either way: the one just added or the copy already in this order.
  const photo = useStore.getState().photos.find((p) => p.filename === h.file.name && p.bytes === h.file.size);
  if (!photo) return false;
  if ((await waitForPhoto(photo.id)) !== 'ready') return false;
  const s = useStore.getState();
  s.setSelection([photo.id]);
  const pick = s.pricing ? resolveHandoffFrame(s.pricing, h) : null;
  if (pick) {
    s.setSpFrameColour(pick.colour);
    s.setApplyBar({ sizeId: pick.sizeId, paper: pick.paper });
    s.setSpPendingFrameId(pick.frameId);
  }
  // The photo is already selected and the frame picked, so the first-visit
  // "tap photos to select them" tour would only stand between them and Add.
  s.markGuideSeen();
  s.setStep(1);
  return true;
}
